import { Dispatch, FormEvent, SetStateAction } from "react"

import { useLazyGetCountriesQuery, useLazyGetStatesQuery, useLazyGetZipCodesQuery } from "store/api/dictionaryApi"
import { UITypes } from "types"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import CitySuburbSelect from "components/form/CitySuburbSelect"
import { InputNew } from "components/form/InputNew"
import { SelectDynamic } from "components/form/SelectDynamic"

import styles from "./index.module.scss"

type AddressValues = {
  address1?: string
  address2?: string
  country?: UITypes.Option[]
  zip?: UITypes.Option[]
  state?: UITypes.Option[]
  city?: UITypes.Option[]
  suburb?: UITypes.Option[]
}

interface Props<T extends AddressValues> {
  inputValues: T
  error: {
    field: string
    message: string
  }
  onInputChange: (event: FormEvent<HTMLInputElement>) => void
  setInputValues: Dispatch<SetStateAction<T>>
}

export const AddressFields = <T extends AddressValues>({
  inputValues,
  error,
  onInputChange,
  setInputValues,
}: Props<T>) => {
  const addressLabels = getAddressLabelsByCountry(inputValues.country?.[0]?.label)

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (inputValues.country?.[0]?.value === country[0]?.value) {
      return
    }

    setInputValues({ ...inputValues, country, zip: [], state: [], city: [], suburb: [] })
  }

  const renderCitySuburb = () => {
    const showSuburb = inputValues.country?.[0]?.value === 3
    const entity = showSuburb ? "suburb" : "city"
    const values = showSuburb ? inputValues.suburb : inputValues.city

    return (
      <CitySuburbSelect
        showSuburb={showSuburb}
        zip_code={inputValues.zip?.[0]?.label || ""}
        country={inputValues.country?.[0]?.label}
        values={values || []}
        onChange={value => setInputValues({ ...inputValues, [entity]: value })}
        error={error.field === entity ? error.message : ""}
      />
    )
  }

  return (
    <>
      <div className={styles.row}>
        <InputNew
          label="Address 1"
          placeholder="Enter address"
          name="address1"
          value={inputValues.address1}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "address1" ? error.message : ""}
        />
        <InputNew
          label="Address 2"
          placeholder="Enter address"
          name="address2"
          value={inputValues.address2}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "address2" ? error.message : ""}
        />
      </div>
      <div className={styles.row}>
        <SelectDynamic
          label="Country"
          searchFieldName="country"
          valueName="id"
          labelName="name"
          name="country"
          values={inputValues.country || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetCountriesQuery}
          placeholder="Enter Country"
          onChange={handleCountryChange}
          errorMessage={error.field === "country" ? error.message : ""}
        />
        <SelectDynamic
          label={addressLabels.zip}
          searchFieldName="code"
          valueName="id"
          labelName="code"
          name="zip"
          values={inputValues.zip || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetZipCodesQuery}
          placeholder={`Enter ${addressLabels.zip}`}
          onChange={value => setInputValues({ ...inputValues, zip: value })}
          errorMessage={error.field === "zip" ? error.message : ""}
          queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
        />
      </div>
      <div className={styles.row}>
        <SelectDynamic
          label={addressLabels.state}
          placeholder={`Enter ${addressLabels.state}`}
          name="state"
          valueName="id"
          labelName="name"
          values={inputValues.state || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetStatesQuery}
          queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
          onChange={value => setInputValues({ ...inputValues, state: value })}
          errorMessage={error.field === "state" ? error.message : ""}
        />
        {renderCitySuburb()}
      </div>
    </>
  )
}
